import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography
} from "@mui/material";
import TrashIcon from "../../../icons/TrashIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import { Calendar } from "react-big-calendar";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Appointment } from "../../../state/appointments";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
import {
  calendarWrapperSx,
  fullWidthLayout,
  eventStyleGetter,
  calendarEvents,
  CalendarEventLabel
} from "./calendarShared";
import AgendaTableView from "./AgendaTableView";
import { PictureMapContext } from "./AppointmentsTable";

interface Props {
  /** Next few upcoming appointments shown as cards (already sliced). */
  upcomingAppointments: Appointment[];
  /** All appointments — used as calendar events (cancelled ones are excluded). */
  appointments: Appointment[];
  /** Twilio room is being created (start buttons disabled). */
  loading: boolean;
  onStart: (patientName: string, id: string) => void;
  onCancel: (id: string) => void;
  /** Open the details modal for an appointment (done or scheduled). */
  onOpenDetails: (id: string) => void;
  /** patientName (lowercased) → picture URL, for the agenda avatars. */
  pictureMap: Record<string, string>;
  // react-big-calendar plumbing built by the parent.
  localizer: any;
  calendarMessages: any;
  defaultScrollTime: Date;
}

const sectionTitleSx = {
  ...typography.heading.s,
  color: semanticColors.text.primary,
  mb: 1
};

const cardNameSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

const cardDateSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary,
  mb: 1
};

const startBtnSx = (loading: boolean) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  background: loading
    ? semanticColors.surface.container
    : semanticColors.gradient.surface,
  borderRadius: "40px",
  height: "36px",
  border: "none",
  cursor: loading ? "not-allowed" : "pointer",
  opacity: loading ? 0.6 : 1,
  px: "16px"
});

const startLabelSx = {
  ...typography.body.mBold,
  color: semanticColors.text.inverse
};

const cancelBtnSx = { color: semanticColors.status.error, ml: "auto" };

const calendarTitleSx = {
  ...typography.heading.s,
  color: semanticColors.text.primary
};

export default function ConsultationsTab({
  upcomingAppointments,
  appointments,
  loading,
  onStart,
  onCancel,
  onOpenDetails,
  pictureMap,
  localizer,
  calendarMessages,
  defaultScrollTime
}: Props) {
  const { t } = useTranslation();

  return (
    <Box>
      {upcomingAppointments.length > 0 && (
        <Box mb={3}>
          <Typography sx={sectionTitleSx}>
            {t("videoConsultations.upcoming")}
          </Typography>
          <Grid container spacing={2}>
            {upcomingAppointments.map(appt => (
              <Grid item xs={12} sm={6} md={3} key={appt.id}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, cursor: "pointer" }}
                  onClick={() => onOpenDetails(appt.id)}
                >
                  <Typography sx={cardNameSx} noWrap>
                    {appt.patientName}
                  </Typography>
                  <Typography sx={cardDateSx}>
                    {moment(appt.start).format(t("common.datetimeFormat"))}
                  </Typography>
                  <Box display="flex" gap={1} mt={1} alignItems="center">
                    <Box
                      component="button"
                      onClick={e => {
                        e.stopPropagation();
                        !loading && onStart(appt.patientName, appt.id);
                      }}
                      disabled={loading}
                      sx={startBtnSx(loading)}
                    >
                      {loading ? (
                        <CircularProgress
                          size={14}
                          sx={{ color: semanticColors.text.inverse }}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faVideo}
                          style={{
                            fontSize: 14,
                            color: semanticColors.text.inverse
                          }}
                        />
                      )}
                      <Typography sx={startLabelSx}>
                        {t("videoConsultations.start")}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        onCancel(appt.id);
                      }}
                      sx={cancelBtnSx}
                    >
                      <TrashIcon size={16} />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mt: 3, mb: 2 }} />
        </Box>
      )}

      <Box mb={2}>
        <Typography sx={calendarTitleSx}>
          {t("videoConsultations.calendar")}
        </Typography>
      </Box>

      <Box translate="no" sx={calendarWrapperSx}>
        <PictureMapContext.Provider value={pictureMap}>
          <Calendar
            localizer={localizer}
            views={{
              month: true,
              week: true,
              day: true,
              agenda: AgendaTableView
            }}
            events={calendarEvents(appointments)}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 680 }}
            scrollToTime={defaultScrollTime}
            // 30-min labelled groups with 15-min sub-slots (day/week views).
            step={15}
            timeslots={2}
            // Full-width layout so close/sequential consultations don't split into
            // columns (see fullWidthLayout note above).
            dayLayoutAlgorithm={fullWidthLayout}
            // Clicking any event opens the details modal (scheduled ones can be
            // started from there; done ones are view-only).
            onSelectEvent={event => onOpenDetails((event as any).id)}
            messages={calendarMessages}
            eventPropGetter={eventStyleGetter}
            components={{ event: CalendarEventLabel }}
          />
        </PictureMapContext.Provider>
      </Box>
    </Box>
  );
}
